import { isNil } from 'lodash';
import type { CashFlowSummaryRowData, OwnerCashFlowSummary, UseCashFlowSummarySpreadsheetConfigProps } from './types';
import { negateAmount } from '../../../data/cashFlowSign';

// Fallback label for an owner token like "company-7" when no name is known.
const tokenLabel = (token: string) => {
  const [kind, id] = token.split('-');
  return `${kind.charAt(0).toUpperCase()}${kind.slice(1)} ${id}`;
};

// Investments are stored negative but shown positive; net cost basis keeps the stored sign so it reads as proceeds minus investments.
const summaryRow = (id: string, name: string, owner: OwnerCashFlowSummary, isTotal: boolean): CashFlowSummaryRowData => {
  const investments = negateAmount(owner.investments);
  return { id, name, investments, proceeds: owner.proceeds, total: owner.investments + owner.proceeds, grossIrr: owner.grossIrr, isTotal };
};

// Turns per-owner sums into the summary table rows: one row per fund or
// child company sorted by name, then the firm total row when there is anything to show.
export const buildCashFlowSummaryRows = ({
  owners,
  firm,
  ownerNames,
  firmName,
}: UseCashFlowSummarySpreadsheetConfigProps): CashFlowSummaryRowData[] => {
  const rows = Object.entries(owners)
    .map(([token, owner]) => summaryRow(token, ownerNames[token] || tokenLabel(token), owner, false))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (rows.length > 0 || !isNil(firm.grossIrr) || firm.investments !== 0 || firm.proceeds !== 0) {
    rows.push(summaryRow('firm-total', firmName, firm, true));
  }

  return rows;
};
